import React, { useEffect, useRef } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Box, Typography } from '@mui/material';

ChartJS.register(...registerables);

const MachineStatusOverviewChart = ({ data = {} }) => {
    const donutChartRef = useRef(null);
    const donutChartInstance = useRef(null);

    // Extract data from props with defaults
    const {
        operational = 78.5,
        under_maintenance = 12.7,
        out_of_order = 8.8,
        total_machines = 0,
        operational_percentage,
        under_maintenance_percentage,
        out_of_order_percentage
    } = data;

    // Calculate percentages if not provided
    const total = operational + under_maintenance + out_of_order;
    const opPercent = operational_percentage
        ? parseFloat(operational_percentage)
        : total > 0 ? Math.round((operational / total) * 100 * 10) / 10 : 0;
    const maintPercent = under_maintenance_percentage
        ? parseFloat(under_maintenance_percentage)
        : total > 0 ? Math.round((under_maintenance / total) * 100 * 10) / 10 : 0;
    const outPercent = out_of_order_percentage
        ? parseFloat(out_of_order_percentage)
        : total > 0 ? Math.round((out_of_order / total) * 100 * 10) / 10 : 0;

    useEffect(() => {
        const handleResize = () => {
            if (donutChartInstance.current) {
                const isSmall = window.innerWidth < 600;
                donutChartInstance.current.options.layout.padding = {
                    left: isSmall ? 40 : 150,
                    right: isSmall ? 40 : 150,
                    top: isSmall ? 20 : 50,
                    bottom: isSmall ? 20 : 50
                };
                donutChartInstance.current.update();
            }
        };

        if (donutChartRef.current) {
            if (donutChartInstance.current) {
                donutChartInstance.current.destroy();
            }

            const ctx = donutChartRef.current.getContext('2d');
            const percentages = [opPercent, maintPercent, outPercent];
            const counts = [operational, under_maintenance, out_of_order];

            const statusCalloutPlugin = {
                id: 'statusCalloutPlugin',
                afterDraw(chart) {
                    const { ctx, data } = chart;
                    const meta = chart.getDatasetMeta(0);

                    meta.data.forEach((datapoint, index) => {
                        // Skip drawing labels for segments with 0 count
                        if (counts[index] <= 0) return;

                        const { x, y, outerRadius, startAngle, endAngle } = datapoint;
                        const midAngle = (startAngle + endAngle) / 2;
                        const label = data.labels[index];
                        const value = percentages[index];
                        const color = data.datasets[0].borderColor[index];

                        const innerLabelRadius = outerRadius * 0.65;
                        const textX = x + Math.cos(midAngle) * innerLabelRadius;
                        const textY = y + Math.sin(midAngle) * innerLabelRadius - 5;

                        ctx.save();
                        ctx.fillStyle = '#444';
                        ctx.font = '600 14px "Inter", sans-serif';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillText(value + '%', textX, textY);
                        ctx.restore();

                        const isRight = Math.cos(midAngle) > 0;
                        const line1X = x + Math.cos(midAngle) * outerRadius;
                        const line1Y = y + Math.sin(midAngle) * outerRadius;

                        const line2X = x + Math.cos(midAngle) * (outerRadius + 15);
                        const line2Y = y + Math.sin(midAngle) * (outerRadius + 15);

                        const line3X = line2X + (isRight ? 10 : -10);
                        const line3Y = line2Y;

                        ctx.beginPath();
                        ctx.moveTo(line1X, line1Y);
                        ctx.lineTo(line2X, line2Y);
                        ctx.lineTo(line3X, line3Y);
                        ctx.strokeStyle = color;
                        ctx.lineWidth = 1;
                        ctx.stroke();

                        ctx.fillStyle = '#5c5c5c';
                        ctx.font = '400 12px "Inter", sans-serif';
                        ctx.textAlign = isRight ? 'left' : 'right';
                        ctx.textBaseline = 'middle';
                        ctx.fillText(`${label} (${counts[index]})`, line3X + (isRight ? 5 : -5), line3Y);
                    });
                }
            };

            const pseudo3DPlugin = {
                id: 'pseudo3DPlugin',
                beforeDatasetsDraw(chart) {
                    const { ctx } = chart;
                    const meta = chart.getDatasetMeta(0);
                    ctx.save();
                    meta.data.forEach((element, index) => {
                        const { x, y, startAngle, endAngle, outerRadius } = element;
                        const baseColor = chart.data.datasets[0].borderColor[index];

                        ctx.fillStyle = baseColor;
                        for (let i = 1; i <= 10; i++) {
                            ctx.beginPath();
                            ctx.arc(x, y + i, outerRadius, startAngle, endAngle);
                            ctx.lineTo(x, y + i);
                            ctx.fill();
                        }
                    });
                    ctx.restore();
                }
            };

            const isSmall = window.innerWidth < 600;

            donutChartInstance.current = new ChartJS(ctx, {
                type: 'pie',
                data: {
                    labels: ['Operational', 'Under Maintenance', 'Out Of Order'],
                    datasets: [
                        {
                            data: [operational, under_maintenance, out_of_order],
                            backgroundColor: ['#a5d6a7', '#ffcc80', '#ef9a9a'],
                            borderColor: ['#66bb6a', '#ffb74d', '#e57373'],
                            borderWidth: 1.5,
                            hoverOffset: 0,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    rotation: -45,
                    layout: {
                        padding: {
                            left: isSmall ? 40 : 150,
                            right: isSmall ? 40 : 150,
                            top: isSmall ? 20 : 50,
                            bottom: isSmall ? 20 : 50
                        }
                    },
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            enabled: true,
                            callbacks: {
                                label: (context) => {
                                    const idx = context.dataIndex;
                                    return `${context.label}: ${counts[idx]} (${percentages[idx]}%)`;
                                }
                            }
                        },
                        datalabels: { display: false }
                    },
                },
                plugins: [pseudo3DPlugin, statusCalloutPlugin]
            });

            window.addEventListener('resize', handleResize);
        }
        return () => {
            window.removeEventListener('resize', handleResize);
            if (donutChartInstance.current) {
                donutChartInstance.current.destroy();
            }
        };
    }, [data, operational, under_maintenance, out_of_order, opPercent, maintPercent, outPercent]);

    const hasData = total > 0 || total_machines > 0;

    return (
        <div className="chart-container" style={{ position: 'relative', width: '100%', height: '100%' }}>
            {!hasData ? (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%'
                }}>
                    <Typography sx={{ color: '#999', fontSize: '14px' }}>
                        No machine status data available
                    </Typography>
                </Box>
            ) : (
                <canvas ref={donutChartRef} style={{ width: '100%', height: '100%' }}></canvas>
            )}
        </div>
    );
};

export default MachineStatusOverviewChart;
